import { useEffect, useState } from 'react';
import { Activity, Crown, Sparkles, Trophy, UserPlus, CalendarPlus, Flame } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2I — Squad Activity Feed.
 *
 * Ambient network awareness. Renders the last 50 domain events scoped to
 * a single squad. Use case: small panel inside ArenaxSquadDetail showing
 * "Sai joined Saturday Football · Vikram voted you MVP · Strikers FC entered HSR Summer Knockout".
 */
const EVENT_COPY = {
  game_joined: { icon: UserPlus, color: '#06b6d4',
    text: (e) => `joined ${e.payload.sport || 'a game'}` },
  game_created: { icon: CalendarPlus, color: '#a855f7',
    text: (e) => `created a new ${e.payload.sport || 'game'}` },
  post_game_submitted: { icon: Sparkles, color: '#ec4899',
    text: (e) => `submitted post-game signals (${e.payload.wpa_per_player_count || 0} chemistry votes)` },
  mvp_awarded: { icon: Crown, color: '#f59e0b',
    text: (e) => 'awarded an MVP nod 🏆' },
  recurring_generated: { icon: Flame, color: '#10b981',
    text: (e) => 'auto-published the next recurring game' },
  tournament_squad_joined: { icon: Trophy, color: '#f59e0b',
    text: (e) => `entered a tournament` },
  tournament_completed: { icon: Crown, color: '#10b981',
    text: (e) => 'completed a tournament 🏆' },
};

function _timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export default function ArenaxSquadActivityFeed({ squadId, incomingActivity }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!squadId) return;
    api.get(`/arenax/squads/${squadId}/activity?limit=30`)
      .then(r => setActivity(r.data.activity || []))
      .catch(() => setActivity([]))
      .finally(() => setLoading(false));
  }, [squadId]);

  // Phase 2I — prepend any realtime-incoming activity items
  useEffect(() => {
    if (!incomingActivity) return;
    setActivity(prev => [incomingActivity, ...prev].slice(0, 30));
  }, [incomingActivity]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-800 p-5 animate-pulse h-44"
        style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }} />
    );
  }
  if (activity.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-slate-800 p-5"
      data-testid="arenax-squad-activity-feed"
      style={{ background: 'linear-gradient(160deg, rgba(168,85,247,0.05), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-4 w-4 text-purple-300" />
        <span className="text-[10px] tracking-[0.3em] text-purple-300 font-bold uppercase">SQUAD PULSE</span>
      </div>
      <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
        {activity.map((e) => {
          const cfg = EVENT_COPY[e.event];
          if (!cfg) return null;
          const Icon = cfg.icon;
          return (
            <div key={e.id} className="flex items-start gap-2.5"
              data-testid={`arenax-activity-${e.event}-${e.id}`}>
              <div className="h-6 w-6 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: cfg.color + '20', border: `1px solid ${cfg.color}40` }}>
                <Icon className="h-3 w-3" style={{ color: cfg.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-300 leading-snug">
                  <span className="font-bold text-slate-100">{e.payload?.player_name || 'A teammate'}</span>{' '}
                  <span className="text-slate-400">{cfg.text(e)}</span>
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">{_timeAgo(e.at)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
